import {StepItemStatus} from 'ui-library/StepNav';

export interface InstrumentData {
  id: string;
  connectionId: string;
  instrumentQueryId: string;
  filename: string;
  displayFilename: string;
  s3Key: string;
  dataType: string;
  downloadUrl: string;
  createdAt: string;
  modifiedAt: string;
  status: InstrumentFileStatus;
  statusInfo: string | undefined;
}

// Keep in sync with InstrumentFileStatus in services/instruments/src/shared/models/instrument_file.py
// except for ARCHIVED, as we never display ARCHIVED files in the UI
export enum InstrumentFileStatus {
  ERRORED = 'ERRORED',
  NOT_UPLOADED = 'NOT_UPLOADED',
  UPLOADED = 'UPLOADED',
  FORMAT_ERROR = 'FORMAT_ERROR',
}

export const FILE_STATUS_TO_TEXT: {[status in InstrumentFileStatus]: string} = {
  [InstrumentFileStatus.ERRORED]: 'Unavailable',
  [InstrumentFileStatus.NOT_UPLOADED]: 'Pending',
  [InstrumentFileStatus.UPLOADED]: 'Available',
  [InstrumentFileStatus.FORMAT_ERROR]: 'File format error',
};

export function isErrorFileStatus(status: InstrumentFileStatus): boolean {
  return [InstrumentFileStatus.ERRORED, InstrumentFileStatus.FORMAT_ERROR].includes(status);
}

export function isUnselectableFileStatus(
  status: InstrumentFileStatus,
  isConnectionAvailable?: boolean
): boolean {
  return (
    // don't allow selecting NOT_UPLOADED files if the Connection is not available, as we cannot retrieve them
    (isConnectionAvailable === false && status === InstrumentFileStatus.NOT_UPLOADED) ||
    isErrorFileStatus(status)
  );
}

// Keep in sync with InstrumentQueryStatus in services/instruments/src/shared/models/instrument_query.py
export enum InstrumentQueryStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR',
}

export interface InstrumentQuery {
  id: string;
  command: string;
  connectionId: string;
  status: InstrumentQueryStatus;
  info: string;
  params: object;
  values: object;
  createdAt: string;
}

export enum InstrumentDataFileType {
  INTERCHANGE = 'INTERCHANGE', // DEPRECATED
  ORIGINAL = 'ORIGINAL',
  TRANSFORM = 'TRANSFORM', // DEPRECATED
}

export enum StepKey {
  ATTACH = 'ATTACH',
  LOGIN = 'LOGIN',
  PROCESS = 'PROCESS',
}

export interface ImportInstrumentDataModalState {
  statusByStep: {[key in StepKey]: StepItemStatus};
  currentStepKey: StepKey;
  selectedRowIndices: Set<number>;
  selectedProcessInstrumentData: Array<InstrumentData>;
  isImporting: boolean;
  isLoggingIn: boolean;
  instrumentData: Array<InstrumentData>;
  instrumentQueryFilter: Array<string>;
  loginQueryStatus: InstrumentQueryStatus;
  loginUsername: string | null;
}

export const INITIAL_STATE: ImportInstrumentDataModalState = {
  statusByStep: {
    [StepKey.ATTACH]: StepItemStatus.UNVISITED,
    [StepKey.LOGIN]: StepItemStatus.UNVISITED,
    [StepKey.PROCESS]: StepItemStatus.UNVISITED,
  },
  // Login is the first step, if required. Skipped if not needed.
  currentStepKey: StepKey.LOGIN,
  selectedRowIndices: new Set(),
  selectedProcessInstrumentData: [],
  isImporting: false,
  isLoggingIn: false,
  instrumentData: [],
  instrumentQueryFilter: [],
  loginQueryStatus: InstrumentQueryStatus.PENDING,
  loginUsername: null,
};

export const BROWSE_FILES_ADAPTERS = ['chromeleon-adapter'];

export function isBrowseFilesAdapter(adapterId: string | undefined): boolean {
  return adapterId !== undefined && BROWSE_FILES_ADAPTERS.includes(adapterId);
}

export const SNOWFLAKE_ADAPTER_ID = 'snowflake-adapter';

export function isSnowflakeAdapter(adapterId: string | null | undefined): boolean {
  return adapterId === SNOWFLAKE_ADAPTER_ID;
}

// A table available in the connected Snowflake database, as returned in the
// LIST_TABLES query's `values` payload ({tables: SnowflakeTable[]}).
export interface SnowflakeTable {
  schema_name: string;
  table_name: string;
}
